import { NavItem } from '@/components/layout/Navigation/sidebar/nav-item';
import type { SessionListItem } from '@/features/sessions/types';
import { cn } from '@/lib/cn';
import { ChevronRight } from 'lucide-react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { AGENT_HOME_THREAD_ROW_TEXT } from '../agent-home-nav-context';
import { trackNavV3ButtonClicked } from '../nav-v3-analytics';
import { RecentVisitCluster } from './recent-visit-cluster';
import { getRecentVisitLabelParts } from './recent-visit-label';
import { RecentVisitTypeMarker } from './recent-visit-type-marker';
import { useOpenSessionFragment } from './use-open-session-fragment';
import { useRecentVisitHoverTarget } from './use-recent-visit-hover-target';

interface RecentVisitRowProps {
  session: SessionListItem;
}

const DISCLOSURE_DURATION_SECONDS = 0.2;
const DISCLOSURE_EASING = [0.165, 0.84, 0.44, 1] as const;

const DISCLOSURE_TRANSITION = {
  duration: DISCLOSURE_DURATION_SECONDS,
  ease: DISCLOSURE_EASING,
} as const;

/**
 * One recent visit: clicking anywhere on the parent row, including its
 * chevron, toggles the inline fragment cluster (Context / note / Ask-Heidi
 * threads). Hovering the row prefetches the session detail so expansion is
 * warm — skipped for offline-synthetic rows, whose GET would 404.
 *
 * Hovering or focusing the row also brings up a preview card off the nav
 * panel's edge, carrying the patient, the visit time, and three actions. The
 * card itself belongs to `RecentVisitHoverCardHost` — one instance for the
 * whole list, so moving down the rows swaps its content rather than playing a
 * collapse and expand at every step. This row only says when it wants it.
 */
export const RecentVisitRow = ({ session }: RecentVisitRowProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const { prefetchVisit } = useOpenSessionFragment();

  const { primary, patientName } = getRecentVisitLabelParts(session, intl);

  const hoverTarget = useRecentVisitHoverTarget({
    session,
    sessionLabel: primary,
    patientName,
    isExpanded,
  });

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    trackNavV3ButtonClicked({
      label: 'nav_v3_recent_visit_toggle',
      is_enabled: next,
      session_id: session.session_id,
    });
    setIsExpanded(next);
  }, [isExpanded, session.session_id]);

  const disclosureTransition = shouldReduceMotion
    ? { duration: 0 }
    : DISCLOSURE_TRANSITION;

  // `itemClassName` lands on `SidebarMenuItem`, the wrapper `NavItem` renders
  // around its button — not the button itself, which is where the design
  // system's `sidebarMenuButton` rounds to `rounded-[10px]`. The wrapper
  // carries no radius of its own, so match that value explicitly rather than
  // drop it, keeping this row's hover radius identical to a chat row's.
  const visitGroupClassName =
    'rounded-[10px] bg-transparent transition-none hover:bg-surface-accent-hover';

  return (
    <NavItem
      label={primary}
      truncateLabel
      icon={<RecentVisitTypeMarker patientName={patientName} />}
      onClick={toggleExpanded}
      ref={hoverTarget.rowRef}
      onMouseEnter={() => {
        prefetchVisit(session);
        hoverTarget.onMouseEnter();
      }}
      onMouseLeave={hoverTarget.onMouseLeave}
      testId="sidebar-link-recent-visit"
      ariaExpanded={isExpanded}
      className={cn(
        'hover:bg-transparent active:bg-transparent',
        AGENT_HOME_THREAD_ROW_TEXT
      )}
      itemClassName={visitGroupClassName}
      inlineBadge={
        <span
          aria-hidden="true"
          data-expanded={isExpanded}
          data-testid="sidebar-recent-visit-chevron"
          // Hidden at rest for hover-capable pointers only (touch keeps the
          // affordance), revealed on row hover or keyboard focus — same
          // treatment main shipped for the section-header chevrons. The
          // group is `menu-item` (on `SidebarMenuItem`, the non-focusable
          // <li> — see AGENT_HOME_THREAD_ROW_TEXT), so focus-visible must be
          // read via `:has()` rather than the plain `group-focus-visible`
          // form. An expanded row keeps its rotated chevron once the
          // pointer leaves, via `data-[expanded=true]:opacity-100`.
          className="ms-1.5 inline-flex size-4 shrink-0 items-center justify-center text-text-secondary transition-[opacity,transform] duration-200 ease-out-quart group-hover/menu-item:opacity-100 group-has-[:focus-visible]/menu-item:opacity-100 data-[expanded=true]:rotate-90 data-[expanded=true]:opacity-100 motion-reduce:transition-none pointer-fine:sm:opacity-0 rtl:data-[expanded=true]:-rotate-90"
        >
          <ChevronRight className="size-4 rtl:-scale-x-100" />
        </span>
      }
      belowContent={
        <AnimatePresence initial={false}>
          {isExpanded ? (
            <m.div
              key="visit-fragments"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={disclosureTransition}
              className="overflow-hidden"
            >
              <RecentVisitCluster session={session} />
            </m.div>
          ) : null}
        </AnimatePresence>
      }
    />
  );
};
